.shell {
  display: grid;
  grid-template-columns: var(--measure-rail) minmax(0, 1fr);
  align-items: start;

  min-height: 100vh;
}

.sidebar {
  position: sticky;
  top: 0;

  display: flex;
  flex-direction: column;
  gap: var(--space-l);

  padding-block: 14px;
  padding-inline: 14px;
  min-height: 100vh;

  background-color: var(--ground);
  border-inline-end: 1px solid var(--border);
}

.sidebarFolded {
  padding: var(--space-l) var(--space-xs);
}

.railHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-xs);
}

.brand {
  font-size: var(--size-400);
  font-weight: var(--weight-strong);
  letter-spacing: -0.02em;

  color: var(--text);
}

.brandBlock {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

/* The customer under the brand: a consultant in preview must never lose track of whose world this is. */
.brandCustomer {
  font-size: var(--size-100);
  font-weight: var(--weight-medium);
  letter-spacing: 0.01em;

  color: var(--accent-strong);

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.whoami {
  font-size: var(--size-100);

  color: var(--text-muted);

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/*
 * The preview banner sits above the page, not over it: sticky so it survives a
 * long scroll, tinted from the accent so it reads as a state and not an error.
 */
.previewBanner {
  position: sticky;
  top: var(--space-s);
  z-index: 2;

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-s);

  padding: var(--space-xs) var(--space-s);

  font-size: var(--size-200);

  color: var(--accent-strong);
  background-color: var(--accent-soft);

  border: 1px solid color-mix(in oklab, var(--accent-strong) 30%, transparent);
  border-radius: var(--radius-s);
  box-shadow: 0 6px 18px -12px color-mix(in oklab, var(--accent-strong) 60%, transparent);
}

/* A small owner tag beside a row: which customer an entry belongs to. */
.ownerTag {
  display: inline-block;

  padding: 1px var(--space-2xs);

  font-size: var(--size-100);
  font-weight: var(--weight-medium);
  line-height: 1.4;

  color: var(--accent-strong);
  background-color: var(--accent-soft);

  border-radius: var(--radius-s);
  white-space: nowrap;
}

.ownerInternal {
  color: var(--text-muted);
  background-color: var(--surface-sunken);
}

.nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
}

.link {
  display: flex;
  align-items: center;
  gap: var(--space-xs);

  padding: var(--space-2xs) var(--space-xs);

  font-size: var(--size-200);
  font-weight: 500;

  color: var(--text-muted);

  border-radius: var(--radius-s);

  text-decoration: none;
  transition: color var(--duration-fast) var(--ease),
    background-color var(--duration-fast) var(--ease);
}

/* Without a hover state a dark rail gives no sign the links are links. */
.link:hover {
  color: var(--text);
  background-color: rgb(255 255 255 / 6%);
}

/*
 * Sized in ems so the glyph tracks the label rather than the root, and left at
 * currentColor so it picks up the active and hover states for free.
 */
.linkIcon {
  flex: none;

  width: 1.15em;
  height: 1.15em;

  color: currentColor;
}

.linkActive,
.linkActive:hover {
  color: var(--accent-strong);
  background-color: var(--accent-soft);

  font-weight: var(--weight-medium);
}

.footer {
  display: flex;
  flex-direction: column;
  gap: var(--space-s);

  margin-block-start: auto;
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--space-l);

  padding: var(--space-xl) var(--space-l) var(--space-2xl);
  max-width: var(--measure-page);
}

.heading {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
}

.lede {
  font-size: var(--size-300);

  color: var(--text-muted);
}

.columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: var(--space-m);
  align-items: start;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
}

.inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-xs);
}

.mono {
  font-family: var(--font-mono);
  font-size: var(--size-100);

  color: var(--text-muted);

  overflow-wrap: anywhere;
}

.signin {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);

  margin: var(--space-2xl) auto;
  padding: var(--space-xl);
  max-width: 26rem;

  background-color: var(--surface);

  border-radius: var(--radius-m);
}

.note {
  font-size: var(--size-200);

  color: var(--text-muted);
}

.problem {
  font-size: var(--size-200);

  color: var(--band-missing);
}

.violation {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);

  padding: var(--space-s);

  font-size: var(--size-200);

  background-color: var(--band-tint-missing);

  border-inline-start: 3px solid var(--band-missing);
  border-radius: var(--radius-s);
}

.open {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);

  padding: var(--space-s);

  font-size: var(--size-200);

  background-color: var(--band-tint-partial);

  border-inline-start: 3px solid var(--band-partial);
  border-radius: var(--radius-s);
}

.rationale {
  font-style: italic;

  color: var(--text-muted);
}

.excluded {
  font-weight: var(--weight-medium);

  color: var(--band-missing);
}

/*
 * A report, read. One framed region for the builder's preview and the saved
 * report alike: the facts of the evaluation on top, the sections in tabs below.
 */
.reading {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);

  padding: var(--space-l);

  background-color: var(--surface-sunken);

  border-radius: var(--radius-m);
}

.readingHead {
  display: flex;
  flex-wrap: wrap;
  align-items: start;
  justify-content: space-between;
  gap: var(--space-m) var(--space-l);
}

/* The evaluation's facts in one line: as of, formula, live or locked. */
.facts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs) var(--space-xl);

  margin: 0;
}

.fact {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
}

.fact dt {
  font-size: var(--size-100);

  color: var(--text-muted);
}

.fact dd {
  margin: 0;

  font-size: var(--size-300);
  font-weight: var(--weight-medium);
  font-variant-numeric: tabular-nums;
}

/* Live and locked share one shape, so the eye learns one place to look. */
.statusLive,
.statusLocked {
  display: inline-block;

  padding: 1px var(--space-xs);

  font-size: var(--size-200);
  font-weight: var(--weight-medium);
  line-height: 1.5;
  font-variant-numeric: tabular-nums;

  border-radius: var(--radius-s);
  white-space: nowrap;
}

.statusLive {
  color: var(--text-muted);
  background-color: var(--band-tint-unassessed);
}

.statusLocked {
  color: var(--accent-strong);
  background-color: var(--accent-soft);
}

/* One persona's column: raised a step from the reading it sits in, never a card in a card. */
.personaCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);

  padding: var(--space-m);

  background-color: var(--surface);

  border: 1px solid var(--border);
  border-radius: var(--radius-m);
}

.personaName {
  font-size: var(--size-400);
}

.subheading {
  font-size: var(--size-200);
  font-weight: var(--weight-medium);

  color: var(--text-muted);
}

/* The preview is what an evaluation scrolls to, so it keeps clear of the top edge. */
.preview {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);

  scroll-margin-block-start: var(--space-l);
}

/* Two actions side by side in one table cell, kept from wrapping apart. */
.rowActions {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2xs);
}

/*
 * Last in the file on purpose: these override the rules above them, and a media
 * query carries no extra weight of its own, so one placed earlier would simply
 * be overwritten by the base rule it means to correct.
 */
/* Tablet: the rail stops being a column and becomes the top of the page. */
@media (max-width: 60rem) {
  .shell {
    grid-template-columns: minmax(0, 1fr);
  }

  /* No longer a column, so the line that divided them belongs underneath. */
  .sidebar {
    position: static;

    min-height: 0;

    border-inline-end: none;
    border-block-end: 1px solid var(--border);
  }
}

@media (max-width: 40rem) {
  .content {
    padding: var(--space-l) var(--space-m) var(--space-xl);
  }

  .columns {
    grid-template-columns: minmax(0, 1fr);
  }
}

/*
 * Folded: the rail keeps only its toggle, so the content gets the width back.
 * Only while the rail is still a column; below that it is already the page top.
 */
@media (min-width: 60.0625rem) {
  .shellFolded {
    grid-template-columns: auto minmax(0, 1fr);
  }
}

/* A product's facts as a sheet: one label above each value, stacked. */
.sheet {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);

  margin: 0;
}

.sheetRow {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
}

.sheetRow dt {
  font-size: var(--size-100);
  font-weight: var(--weight-medium);

  color: var(--text-muted);
}

.sheetRow dd {
  margin: 0;

  font-size: var(--size-300);
}

.plainList {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);

  margin: 0;
  padding-inline-start: var(--space-m);
}
